import { useEffect, useState } from 'react';
import {
  activeAreas,
  addArea,
  getGoals,
  moveArea,
  removeArea,
  renameArea,
  restoreArea,
  setCell,
} from '../../db/goals';
import { useAsync } from '../hooks/useAsync';
import { useDataVersion } from '../../db/events';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { cellKey, HORIZON_LABELS, HORIZONS, type GoalsDoc, type Horizon } from '../../domain/types';
import { emptyGoals } from '../../db/goals';
import { AddInput } from '../components/CommitInput';

/** 1マス分の入力。フォーカスが外れた時にだけ保存する。 */
function Cell({
  value,
  onSave,
  placeholder,
}: {
  value: string;
  onSave: (text: string) => void;
  placeholder: string;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  return (
    <textarea
      className="goal-cell"
      value={draft}
      placeholder={placeholder}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        if (draft !== value) onSave(draft);
      }}
    />
  );
}

export function GoalsView() {
  const version = useDataVersion();
  const bp = useBreakpoint();
  const goals = useAsync<GoalsDoc>(getGoals, [version], emptyGoals());
  const [showDeleted, setShowDeleted] = useState(false);

  const areas = activeAreas(goals);
  const deleted = goals.areas.filter((a) => a.deleted);
  const text = (h: Horizon, areaId: string) => goals.cells[cellKey(h, areaId)]?.text ?? '';

  return (
    <div className="view">
      <header className="view-head">
        <div className="view-title">
          <h1>目標・ゴール</h1>
          <span className="summary">なりたい状態を、期間と領域で書き出します</span>
        </div>
      </header>

      <div className="view-body goals">
        {bp === 'phone' ? (
          // スマホは領域ごとに縦積み
          <div className="goal-stack">
            {areas.map((a) => (
              <section key={a.id} className="goal-group">
                <div className="goal-group-head">
                  <h2>{a.name}</h2>
                  <div className="cat-actions">
                    <button className="icon-btn" onClick={() => moveArea(a.id, -1)} aria-label="上へ">↑</button>
                    <button className="icon-btn" onClick={() => moveArea(a.id, 1)} aria-label="下へ">↓</button>
                    <button
                      className="icon-btn"
                      onClick={() => {
                        const n = prompt('領域の名前', a.name);
                        if (n && n.trim()) renameArea(a.id, n.trim());
                      }}
                      aria-label="名前を変更"
                    >
                      ✎
                    </button>
                    <button
                      className="icon-btn danger"
                      onClick={() => {
                        if (confirm(`「${a.name}」を一覧から外します。書いた内容は残ります。`)) removeArea(a.id);
                      }}
                      aria-label="削除"
                    >
                      ✕
                    </button>
                  </div>
                </div>
                {HORIZONS.map((h) => (
                  <label key={h} className="field">
                    <span className="field-label">{HORIZON_LABELS[h]}</span>
                    <Cell
                      value={text(h, a.id)}
                      placeholder={`${HORIZON_LABELS[h]}の${a.name}はどうなっていたいか`}
                      onSave={(t) => setCell(h, a.id, t)}
                    />
                  </label>
                ))}
              </section>
            ))}
          </div>
        ) : (
          <div className="goal-grid" style={{ gridTemplateColumns: `160px repeat(${HORIZONS.length}, 1fr)` }}>
            <div className="goal-corner" />
            {HORIZONS.map((h) => (
              <div key={h} className="goal-col-head">
                {HORIZON_LABELS[h]}
              </div>
            ))}
            {areas.map((a, i) => (
              <div key={a.id} className="goal-row">
                <div className="goal-row-head">
                  <span className="goal-area-name">{a.name}</span>
                  <div className="cat-actions">
                    <button className="icon-btn" onClick={() => moveArea(a.id, -1)} disabled={i === 0} aria-label="上へ">↑</button>
                    <button className="icon-btn" onClick={() => moveArea(a.id, 1)} disabled={i === areas.length - 1} aria-label="下へ">↓</button>
                    <button
                      className="icon-btn"
                      onClick={() => {
                        const n = prompt('領域の名前', a.name);
                        if (n && n.trim()) renameArea(a.id, n.trim());
                      }}
                      aria-label="名前を変更"
                    >
                      ✎
                    </button>
                    <button
                      className="icon-btn danger"
                      onClick={() => {
                        if (confirm(`「${a.name}」を一覧から外します。書いた内容は残ります。`)) removeArea(a.id);
                      }}
                      aria-label="削除"
                    >
                      ✕
                    </button>
                  </div>
                </div>
                {HORIZONS.map((h) => (
                  <Cell
                    key={h}
                    value={text(h, a.id)}
                    placeholder={`${HORIZON_LABELS[h]}の${a.name}`}
                    onSave={(t) => setCell(h, a.id, t)}
                  />
                ))}
              </div>
            ))}
          </div>
        )}

        <div className="goal-add">
          <AddInput
            placeholder="領域を追加（例: 学び・健康）"
            maxLength={20}
            onAdd={(value) => addArea(value)}
          />
          {deleted.length > 0 && (
            <div className="btn-row">
              <button className="ghost-btn" onClick={() => setShowDeleted((v) => !v)}>
                外した領域（{deleted.length}）を{showDeleted ? '隠す' : '表示'}
              </button>
            </div>
          )}
        </div>

        {showDeleted && (
          <ul className="cat-list">
            {deleted.map((a) => (
              <li key={a.id} className="is-deleted">
                <span className="cat-name">{a.name}</span>
                <span className="cat-actions">
                  <button className="icon-btn" onClick={() => restoreArea(a.id)}>
                    戻す
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}

        <p className="muted">
          入力欄から離れると自動で保存されます。ここに書いた内容は、週次レビューでAIが参照します。
        </p>
      </div>
    </div>
  );
}
